import axios from "axios";

const ACCESS_TOKEN_KEY = "om.accessToken";
const authHttp = axios.create({
  baseURL: (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api",
  withCredentials: true,
  headers: { Accept: "application/json" },
});
let accessTokenMemory = null;
let authRevision = 0;
let refreshInFlight = null;
let authChange = null;
const clearedListeners = new Set();

export function getAccessToken() {
  return accessTokenMemory || localStorage.getItem(ACCESS_TOKEN_KEY) || null;
}

function storeToken(token) {
  accessTokenMemory = token || null;
  if (token) localStorage.setItem(ACCESS_TOKEN_KEY, token);
  else localStorage.removeItem(ACCESS_TOKEN_KEY);
}

export function setAuthToken(token) {
  authRevision++;
  storeToken(token);
}

export function clearAuth() {
  authRevision++;
  storeToken(null);
  for (const listener of clearedListeners) listener();
}

export const getAuthRevision = () => authRevision;
export function subscribeAuthCleared(listener) {
  clearedListeners.add(listener);
  return () => clearedListeners.delete(listener);
}

function extractAccess(data) {
  const access = data?.accessToken || data?.token || data?.jwt || data?.access_token;
  if (!access) throw new Error("El servidor no devolvió un token de acceso.");
  return access;
}

function sessionChanged() {
  return new axios.CanceledError("La sesión cambió durante la solicitud.");
}

export async function login({ username, password }) {
  clearAuth();
  const change = { revision: authRevision };
  authChange = change;
  try {
    // Wait for an older refresh cookie before letting login replace it.
    await refreshInFlight?.promise.catch(() => {});
    if (authChange !== change || authRevision !== change.revision) throw sessionChanged();
    const { data } = await authHttp.post("/auth/login", { username, password });
    if (authChange !== change || authRevision !== change.revision) throw sessionChanged();
    setAuthToken(extractAccess(data));
    return data;
  } finally {
    if (authChange === change) authChange = null;
  }
}

export function refreshAccessToken() {
  if (authChange) return Promise.reject(sessionChanged());
  const revision = authRevision;
  if (refreshInFlight?.revision === revision) return refreshInFlight.promise;
  const operation = { revision };
  operation.promise = authHttp.post("/auth/refresh")
    .then(({ data }) => {
      if (authRevision !== revision) throw sessionChanged();
      const access = extractAccess(data);
      storeToken(access);
      return access;
    })
    .catch(error => {
      if (authRevision === revision && [401, 403].includes(error?.response?.status)) clearAuth();
      throw error;
    })
    .finally(() => {
      if (refreshInFlight === operation) refreshInFlight = null;
    });
  refreshInFlight = operation;
  return operation.promise;
}

export async function logout() {
  clearAuth();
  const change = { revision: authRevision };
  authChange = change;
  try {
    await refreshInFlight?.promise.catch(() => {});
    if (authChange === change) await authHttp.post("/auth/logout");
  } catch {
    // Local logout still succeeds when the server is unavailable.
  } finally {
    if (authChange === change) authChange = null;
  }
}

export function initAuth() {
  accessTokenMemory = localStorage.getItem(ACCESS_TOKEN_KEY) || null;
}

export async function trySilentRefreshOnBoot() {
  if (getAccessToken() || ["/login", "/access"].includes(window.location.pathname)) return;
  try {
    await refreshAccessToken();
  } catch {
    // No usable refresh cookie: continue to the login screen.
  }
}
